import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOptions } from '../UI';
import type {
  InterventionRoomResponse,
  RoomBookingRequest,
  RoomBookingResponse,
  StaffProfessionResponse
} from '../../api/generated';
import {
  StaffCompetenceRequirementsEditor,
  type FlexRequirementDraft,
  type MandatoryRequirementDraft
} from './StaffCompetenceRequirementsEditor';
import {
  BookingFormModalShell,
  BookingOccurrenceFields,
  useBookingFormState
} from '../Bookings/shared';
import { ROOM_BOOKING_TYPE } from './roomBookingConstants';

interface RoomBookingFormModalProps {
  booking: RoomBookingResponse | null;
  rooms: InterventionRoomResponse[];
  professions: StaffProfessionResponse[];
  onSave: (payload: RoomBookingRequest) => void;
  onClose: () => void;
}

const KEY_PREFIX = 'room-booking';

export const RoomBookingFormModal = ({
  booking,
  rooms,
  professions,
  onSave,
  onClose
}: RoomBookingFormModalProps) => {
  const { t } = useTranslation();
  const form = useBookingFormState({ booking, keyPrefix: KEY_PREFIX, canBeAllDay: true });
  const [roomId, setRoomId] = useState<string | null>(booking?.roomId ?? null);
  const [mandatory, setMandatory] = useState<MandatoryRequirementDraft[]>(() =>
    (booking?.mandatoryStaffCompetenceRequirements ?? []).map((req) => ({
      ...req,
      _key: crypto.randomUUID()
    }))
  );
  const [flex, setFlex] = useState<FlexRequirementDraft[]>(() =>
    (booking?.flexStaffCompetenceRequirements ?? []).map((req) => ({
      ...req,
      _key: crypto.randomUUID()
    }))
  );

  const selectedRoom = rooms.find((room) => room.id === roomId) ?? null;

  const handleSave = () => {
    const isValid = form.validate((next) => {
      if (!roomId) next.roomId = t('room-booking-error-room');
    });
    if (!isValid || !roomId) return;
    onSave({
      type: ROOM_BOOKING_TYPE,
      reason: form.reason.trim() || undefined,
      roomId,
      time: form.buildTime(),
      mandatoryStaffCompetenceRequirements: mandatory.map((req) => ({
        staffProfessionIds: req.staffProfessionIds,
        count: req.count,
        preProcessingMinutes: req.preProcessingMinutes,
        postProcessingMinutes: req.postProcessingMinutes
      })),
      flexStaffCompetenceRequirements: flex.map((req) => ({
        staffProfessionId: req.staffProfessionId,
        count: req.count
      }))
    });
  };

  return (
    <BookingFormModalShell
      keyPrefix={KEY_PREFIX}
      isEditing={booking !== null}
      onSave={handleSave}
      onClose={onClose}
    >
      <Dropdown
        label={t('room-booking-room-label')}
        required
        placeholder={t('room-booking-room-placeholder')}
        error={Boolean(form.errors.roomId)}
        helperText={form.errors.roomId}
        selectedOption={selectedRoom?.description}
      >
        <DropdownOptions
          options={rooms}
          selectedOption={selectedRoom}
          getKey={(room) => room.id}
          displayOption={(room) => room.description}
          handleChange={(room) => setRoomId(room.id)}
        />
      </Dropdown>

      <BookingOccurrenceFields form={form} isCreating={booking === null} keyPrefix={KEY_PREFIX} />

      <StaffCompetenceRequirementsEditor
        professions={professions}
        mandatory={mandatory}
        flex={flex}
        onMandatoryChange={setMandatory}
        onFlexChange={setFlex}
      />
    </BookingFormModalShell>
  );
};
